function safeCapture(diagnostics,scope,error,meta={}){
  try{diagnostics?.capture?.(scope,error,meta);}catch{}
}

const OAUTH_PENDING_PROVIDER_KEY='dymistrz.oauth.pending_provider';
const SAFE_OAUTH_PROVIDERS=new Set(['google','apple']);

function safeOAuthProvider(value){
  const provider=String(value||'').trim().toLowerCase();
  return SAFE_OAUTH_PROVIDERS.has(provider)?provider:null;
}

function safeOAuthErrorCode(value){
  const code=String(value||'').trim().toUpperCase().replace(/[^A-Z0-9_.-]/g,'_').slice(0,80);
  return code||'OAUTH_PROVIDER_FAILED';
}

function callbackParams(input){
  let url;
  try{url=new URL(String(input||''));}catch{return null;}
  const query=url.searchParams;
  const hash=new URLSearchParams(String(url.hash||'').replace(/^#/,''));
  return {url,query,hash};
}

export function parseOAuthCallbackError(input){
  const parsed=callbackParams(input);
  if(!parsed)return null;
  const rawError=parsed.query.get('error')||parsed.hash.get('error');
  const rawCode=parsed.query.get('error_code')||parsed.hash.get('error_code')||rawError;
  if(!rawError&&!rawCode)return null;
  return Object.freeze({code:safeOAuthErrorCode(rawCode)});
}

export function parseMagicLinkCallback(input){
  let url;
  try{url=new URL(String(input||''));}catch{return null;}
  const tokenHash=String(url.searchParams.get('token_hash')||'').trim();
  const type=String(url.searchParams.get('type')||'').trim().toLowerCase();
  if(!tokenHash||type!=='email')return null;
  return Object.freeze({tokenHash,type:'email'});
}

export function createAuthActions({
  auth,
  diagnostics,
  origin=globalThis.location?.origin||'',
  currentUrl=()=>globalThis.location?.href||'',
  replaceUrl=url=>globalThis.history?.replaceState?.({},'',url),
  oauthStorage=globalThis.sessionStorage,
  isOnline=()=>globalThis.navigator?.onLine!==false,
  confirmGlobal=()=>Boolean(globalThis.confirm?.('Wylogować konto na wszystkich urządzeniach?'))
}={}){
  if(!auth)throw new TypeError('Auth adapter is required');

  function setPendingOAuthProvider(provider){
    try{oauthStorage?.setItem?.(OAUTH_PENDING_PROVIDER_KEY,provider);}catch{}
  }

  function pendingOAuthProvider(){
    try{return safeOAuthProvider(oauthStorage?.getItem?.(OAUTH_PENDING_PROVIDER_KEY));}
    catch{return null;}
  }

  function clearPendingOAuthProvider(){
    try{oauthStorage?.removeItem?.(OAUTH_PENDING_PROVIDER_KEY);}catch{}
  }

  async function requestMagicLink(email){
    const redirectTo=origin?new URL('/',origin).toString():undefined;
    await auth.signInWithMagicLink(email,{redirectTo});
    return Object.freeze({ok:true,status:'email_sent'});
  }

  async function signInWithOAuth(provider){
    const selected=safeOAuthProvider(provider);
    if(!selected)throw new TypeError('Unsupported OAuth provider');
    const redirectTo=origin?new URL('/',origin).toString():undefined;
    setPendingOAuthProvider(selected);
    try{
      await auth.signInWithOAuth(selected,{redirectTo});
      return Object.freeze({ok:true,status:'oauth_started',provider:selected});
    }catch(error){
      clearPendingOAuthProvider();
      safeCapture(diagnostics,'auth.oauth.start',error,{provider:selected,code:safeOAuthErrorCode(error?.code||error?.name)});
      error.safeOAuthCode=safeOAuthErrorCode(error?.code||error?.name);
      throw error;
    }
  }

  function consumeOAuthCallbackError(){
    const parsed=parseOAuthCallbackError(currentUrl());
    if(!parsed)return Object.freeze({handled:false,status:'none'});
    const provider=pendingOAuthProvider();
    clearPendingOAuthProvider();
    if(origin)replaceUrl(new URL('/',origin).toString());
    const error=new Error('OAuth callback failed');
    safeCapture(diagnostics,'auth.oauth.callback',error,{provider,code:parsed.code});
    return Object.freeze({handled:true,status:'oauth_error',provider,code:parsed.code});
  }

  async function consumeMagicLinkCallback(){
    const parsed=parseMagicLinkCallback(currentUrl());
    if(!parsed)return Object.freeze({handled:false,status:'none'});
    try{
      const session=await auth.verifyMagicLinkTokenHash(parsed.tokenHash);
      if(origin)replaceUrl(new URL('/',origin).toString());
      return Object.freeze({handled:true,status:'verified',session});
    }catch(error){
      safeCapture(diagnostics,'auth.magic-link.callback',error);
      if(origin)replaceUrl(new URL('/',origin).toString());
      return Object.freeze({handled:true,status:'invalid',session:null});
    }
  }

  async function signOutLocal(){
    await auth.signOutLocal();
    return Object.freeze({ok:true,status:'signed_out'});
  }

  async function signOutAllDevices(){
    if(!isOnline())return Object.freeze({ok:false,status:'offline'});
    if(!confirmGlobal())return Object.freeze({ok:false,status:'cancelled'});
    await auth.signOutGlobal();
    return Object.freeze({ok:true,status:'signed_out_all'});
  }

  return Object.freeze({
    requestMagicLink,
    signInWithOAuth,
    consumeOAuthCallbackError,
    consumeMagicLinkCallback,
    signOutLocal,
    signOutAllDevices
  });
}
